import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { ThemeToggleIconOnly } from '../components/common/theme-toggle';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  storeOAuthState,
  buildAuthorizeUrl,
} from '../lib/utils';
import {
  OAUTH2_CLIENT_ID,
  OAUTH2_REDIRECT_URI,
  OAUTH2_SCOPES,
  API_BASE
} from '../lib/env';

export function LoginScreen() {
  const { error, clearError } = useAuth();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Initialize dark mode on login page
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Handle OAuth2 logout return (verify state parameter for security)
  useEffect(() => {
    const state = searchParams.get('state');
    if (state) {
      // Verify the state matches what we sent during logout
      const storedState = sessionStorage.getItem('oauth_logout_state');
      if (storedState && storedState === state) {
        sessionStorage.removeItem('oauth_logout_state');
      } else {
        sessionStorage.removeItem('oauth_logout_state');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }
    clearError();
  }, [isAuthenticated, navigate, clearError]);

  const handleOAuthLogin = async () => {
    try {
      setLoading(true);
      clearError();

      // Generate PKCE parameters
      const codeVerifier = await generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      // Store state for verification after callback
      storeOAuthState(state, codeVerifier);

      // Build authorization URL
      const authorizeEndpoint = `${API_BASE}/oauth2/authorize`;
      const authUrl = buildAuthorizeUrl(
        authorizeEndpoint,
        OAUTH2_CLIENT_ID,
        OAUTH2_REDIRECT_URI,
        codeChallenge,
        state,
        OAUTH2_SCOPES
      );

      // Redirect to OAuth2 provider
      window.location.href = authUrl;
    } catch {
      clearError();
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 overflow-y-auto">
      {/* Animated Background Elements - Mobile Optimized */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 left-4 w-48 h-48 md:w-72 md:h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 right-4 w-48 h-48 md:w-72 md:h-72 bg-secondary-200 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-4 left-1/2 w-48 h-48 md:w-72 md:h-72 bg-accent-200 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Login Card - Mobile Optimized */}
      <div className="w-full max-w-md animate-scale-in">
        <div className="modern-card-gradient overflow-hidden">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 p-6 md:p-8 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-md rounded-xl p-2 shadow-inner flex-shrink-0">
                  <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Welcome Back</h2>
              </div>
              <ThemeToggleIconOnly />
            </div>
            <p className="text-primary-100 text-sm md:text-base">
              Sign in to your AuthKeystone admin account
            </p>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 space-y-4 md:space-y-6">
            {error && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 animate-slide-in">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-danger-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-danger-800">Login Failed</h3>
                    <p className="text-sm text-danger-700 mt-1 break-words">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleOAuthLogin}
              disabled={loading}
              className="w-full btn-primary btn-ripple group relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-base md:text-lg font-semibold">
                {loading ? (
                  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                )}
                {loading ? 'Redirecting...' : 'Login with OAuth2'}
              </span>
            </button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-neutral-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>

            <div className="text-center text-sm text-neutral-600 dark:text-neutral-400 space-y-2">
              <p>You will be securely redirected to the authentication provider</p>
              <p className="text-xs text-neutral-400">
                Uses PKCE for enhanced security
              </p>
            </div>

            <div className="pt-4 border-t border-neutral-200 dark:border-gray-700 text-center">
              <Link
                to="/setup"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
              >
                Need to setup super admin?
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-sm text-neutral-500 dark:text-neutral-400">
          <p>🔒 Secure authentication powered by OAuth2 + PKCE</p>
        </div>
      </div>
    </div>
  );
}
